import { useTranslation } from 'react-i18next'

import { Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useColleagues } from '@/features/directories/api/useColleagues'
import { useRoleLabel } from '@/features/settings/api/useRoleNames'
import { describeProblem, fieldErrorKey, fieldErrors } from '@/lib/problems'
import type { Client } from '@/types/domain'

/* Hands a client to another salesperson — the "Change sales contact" act.
 *
 * The picker is the staff ROSTER (useColleagues, GET /api/users): every account
 * that can sign in, fetched only once the sheet opens. It is deliberately not
 * narrowed to "people who can hold clients" here. That rule — holds
 * directory.client.create, and a role that reaches the clients surface — is the
 * server's, resolved from permissions this client does not see for anybody but
 * itself; a copy here would drift from it and hide the very colleague the
 * founder meant. So the server answers, and a refused choice comes back as a
 * sentence under the picker (422 `ownerId: ['not-eligible']`).
 *
 * The current sales contact is left out of the list: choosing them is a save
 * that changes nothing.
 */
export function ClientOwnerSheet({
  open,
  onClose,
  client,
  currentOwnerName,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  client: Client
  /** The present sales contact's name, for the line that says who it is now. */
  currentOwnerName?: string
  pending?: boolean
  /** The mutation's error, raw: a field refusal is worded under the picker,
   *  anything else above the fields. */
  error: unknown
  onConfirm: (ownerId: string) => void
}) {
  const { t } = useTranslation()
  const { data: colleagues, isPending: loading } = useColleagues({ enabled: open })
  // A custom role by its served name, not its key (story B3); read only once
  // the sheet opens, like the roster.
  const roleName = useRoleLabel({ enabled: open })
  const [draft, setDraft, , , dirty] = useFormDraft<{ ownerId: string }>(
    open ? client.id : null,
    () => ({ ownerId: '' }),
  )

  const options = (colleagues ?? [])
    .filter((person) => person.id !== client.ownerId)
    .map((person) => {
      return {
        value: person.id,
        label: person.name,
        hint: roleName(person.role),
      }
    })

  /* The field's own refusal is the common one and names what to do — pick
     somebody else. Only a refusal on some OTHER ground goes above the fields. */
  const ownerCode = fieldErrors(error)['ownerId']?.[0]
  const fieldError =
    ownerCode === undefined
      ? undefined
      : ownerCode === 'not-eligible'
        ? t('clients.owner.notEligible')
        : t(fieldErrorKey(ownerCode))
  const recordError =
    error && ownerCode === undefined ? t(describeProblem(error).detailKey) : undefined

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('clients.owner.title')}
      submitLabel={t('clients.owner.confirm')}
      pending={pending}
      error={recordError}
      dirty={dirty}
      /* Disabled only while nothing is chosen, a state the reader can see the
         cause of — the picker above the button is empty. */
      disabled={draft.ownerId === ''}
      onSubmit={() => {
        if (draft.ownerId === '') return
        onConfirm(draft.ownerId)
      }}
    >
      <div className="bg-surface-sunken border-line flex flex-col gap-1 rounded-md border px-3 py-2.5">
        <span className="asa-eyebrow">{t('clients.people.salesContact')}</span>
        <span className="text-text-heading text-[13px]">
          {currentOwnerName ?? t('common.empty')}
        </span>
      </div>

      <Field label={t('clients.owner.colleague')} required error={fieldError}>
        {(props) => (
          <Select
            value={draft.ownerId}
            onChange={(ownerId) => setDraft({ ownerId })}
            options={options}
            placeholder={loading ? t('common.loading') : t('common.form.selectPlaceholder')}
            searchable
            {...props}
          />
        )}
      </Field>

      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('clients.owner.hint')}
      </p>
    </FormSheet>
  )
}
